/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import { declareEntity } from '../fw/MetaDataHelper';
import * as Permission from '../util/Permission';

export default declareEntity({
    entity: "entityDefs",
    graphQL: "EntityDef",
    name: "Entity Definition",
    dynamoPrefix: 'def',
    storageType: 'schema',
    writePermission: [
        Permission.PERM_EDIT_CHANNEL_DEF
    ],
    parentRelation: "groupDefs",
    defaultOrder: ['groupDef_id', 'name', 'key'],
    defaultTitle: (data) => {
        return data['name'];
    },
    isPublic: true,
    onModify: ['groupDef'],
    fields: (types) => { return {
        id: {
            name: "Entity Definition ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            userEditable: false,
            isPublic: true,
        },
        name: {
            name: "Name",
            type: "string",
            trim: true,
            translatable: true,
            defaultValue: "",
            required: true,
            isPublic: true,
        },
        icon: {
            name: "Icon",
            type: "string",
            description: "Use icons from font-awesome in form 'fa-users'",
            trim: true,
            defaultValue: "",
            isPublic: true,
        },
        inheritedIcon: {
            name: "Icon",
            type: "string",
            controllerFn: 'getInheritedIcon',
            userEditable: false,
            import: false,
            export: false,
            defaultValue: "",
            isPublic: true,
        },
        pluralName: {
            name: "Plural Name",
            type: "string",
            trim: true,
            translatable: true,
            defaultValue: "",
        },
        key: {
            name: "Key",
            trim: true,
            type: "string",
            defaultValue: (groupDef) => {
                return groupDef['key'] + ".entity";
            },
            validations: {
                matchRegexp: /^[a-zA-Z0-9\.\-_]+$/,
            },
            isPublic: true,
        },
        deprecated: {
            name: "Deprecated",
            type: "boolean",
            defaultValue: false,
        },
        groupDef: {
            name: "Group",
            type: "relation",
            to: () => types['groupDefs'],
            defaultValue: (groupDef) => {
                return groupDef['id'];
            },
            required: true,
            parent: true,
            hiddenCreate: true,
            isPublic: true,
        },
        channelDef: {
            name: "Application Definition",
            to: () => types['channelDefs'],
            defaultValue: (groupDef) => {
                return groupDef && 'channelDef_id' in groupDef ? groupDef['channelDef_id'] : "";
            },
            type: "relation",
            isPublic: true,
        },
        description: {
            name: "Description",
            translatable: true,
            type: "text",
            defaultValue: ""
        },
        isComponent: {
            name: "Is component?",
            type: "boolean",
            defaultValue: false
        },
        isEntity: {
            name: "Is entity?",
            type: "boolean",
            defaultValue: true
        },
        author: {
            name: "Author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        swaggerId: {
            name: "Swagger ID",
            type: "string",
        },
        defaultView: {
            name: "Default view",
            type: "relation",
            to: () => types['entityViewDefs'],
            userEditable: false,
            export: false,
            import: false,
            controllerFn: "getDefaultView",
        },
        defaultList: {
            name: "Default list",
            type: "relation",
            to: () => types['entityViewDefs'],
            userEditable: false,
            export: false,
            import: false,
            controllerFn: "getDefaultList",
        }
    }; },
    relations: (types) => { return {
        fieldDefs: {
            type: 'oneToMany',
            to: types['fieldDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        computedFieldDefs: {
            type: 'oneToMany',
            to: types['computedFieldDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        allFieldDefs: {
            type: 'handler',
            to: types['fieldDefs'],
            controllerFn: 'getAllFieldDefs',
            isPublic: true,
        },
        allParents: {
            type: 'handler',
            description: "Return all parents (is-a and has-a)",
            controllerFn: 'getAllParents',
            to: types['entityDefs'],
        },
        allParentEntities: {
            type: 'handler',
            description: "Return all parents that are used as entities (is-a)",
            controllerFn: 'getAllParentEntities',
            to: types['entityDefs'],
        },
        entityViewDefs: {
            type: 'oneToMany',
            to: types['entityViewDefs'],
            export: false,
            foreignKeys: [
                {
                    from: "id",
                    to: "entityDef_id"
                }
            ]
        },
        screenDefs: {
            type: 'oneToMany',
            to: types['screenDefs'],
            export: false,
            foreignKeys: [
                {
                    from: "id",
                    to: "entityDef_id"
                }
            ]
        },
        allScreenDefs: {
            type: 'oneToMany',
            to: types['screenDefs'],
            export: false,
            controllerFn: 'getAllScreenDefs',
        },
        allAutoCreateScreenDefs: {
            type: 'oneToMany',
            to: types['screenDefs'],
            export: false,
            controllerFn: 'getAllAutoCreateScreenDefs',
        },
        allEntityViewDefs: {
            type: 'oneToMany',
            to: types['entityViewDefs'],
            export: false,
            controllerFn: 'getAllEntityViewDefs',
        },
        allBoxesToAdd: {
            type: 'oneToMany',
            to: types['screenBoxToAdd'],
            export: false,
            controllerFn: 'getScreenBoxesToAdd',
        },
        entityInheritanceDefs: {
            type: 'oneToMany',
            to: types['entityInheritanceDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        entityRelationDefs: {
            type: 'oneToMany',
            to: types['entityRelationDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        allEntityRelationDefs: {
            type: 'oneToMany',
            to: types['entityRelationDefs'],
            controllerFn: 'getAllEntityRelationDefs',
        },
        allEntityDefsToAddAsInheritance: {
            type: 'oneToMany',
            to: types['entityDefs'],
            controllerFn: 'getAllEntityDefsToAddAsInheritance',
        },
        operationDefs: {
            type: 'oneToMany',
            to: types['operationDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        allOperationDefs: {
            type: 'oneToMany',
            to: types['operationDefs'],
            controllerFn: 'getAllOperationDefs',
        },
    }; }
});
